import { useEffect, useState } from 'react';

import { toast } from '../common/Toast/toast';
import PopupDone, { usePopupDone } from '../common/PopupDone/PopupDone.jsx';
import {
  accessErrorMessage,
  approveAccessRequest,
  rejectAccessRequest,
} from '../../../services/AccessRequestService';
import { StatusPill, formatWhen, roleNames } from './accessStatus.jsx';

/**
 * One access request -- the administrator's view popup.
 *
 * Built to view_popup_rules in instruction_ui.json, like the
 * /list-attendance record popup: the same frame, head, sections and footer,
 * with the decision in the footer beside Close. `record` is the list row,
 * which already carries everything shown here.
 */
const AccessRequestModal = ({ record, onClose, onDecided }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');

  // A decision swaps the request for the done panel, which closes the
  // popup five seconds later.
  const { done } = usePopupDone(Boolean(record));

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!record) return null;

  const name = record.userName || record.userEmail || 'Unknown user';
  const role = record.requestedRoleLabel || 'the requested role';
  const open = record.status === 'pending';

  const decide = async (kind) => {
    if (busy) return;
    setBusy(kind);
    try {
      if (kind === 'approve') {
        await approveAccessRequest(record.id);
        toast.success(`${role} granted to ${name}`);
      } else {
        await rejectAccessRequest(record.id, note.trim());
        toast.success('Request declined');
      }
      // Reloads the list behind; the popup stays up showing the done panel.
      onDecided();
    } catch (error) {
      toast.error(accessErrorMessage(error, kind === 'approve' ? 'Could not approve the request' : 'Could not decline the request'));
      setBusy('');
    }
  };

  return (
    <div className="pg-modal" role="dialog" aria-modal="true" aria-labelledby="pgm-access-title" onClick={onClose}>
      <div className="pg-modal-panel" onClick={(e) => e.stopPropagation()}>
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="pgm-access-title">
              {name}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Email</dt>
                <dd>{record.userEmail || '—'}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Asks for</dt>
                <dd>{role}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Holds now</dt>
                <dd>{roleNames(record.currentRoles)}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Asked on</dt>
                <dd>{formatWhen(record.createdAt)}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Status</dt>
                <dd>
                  <StatusPill status={record.status} />
                </dd>
              </div>
            </dl>
          </div>

          <button type="button" className="pg-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
            <>
              <section className="pgm-section">
                <h3 className="pgm-section-title">Reason</h3>
                {record.reason ? <p className="acc-text">{record.reason}</p> : <p className="pgm-empty">No reason was given.</p>}
              </section>

              {!open && record.reviewedAt && (
                <section className="pgm-section">
                  <h3 className="pgm-section-title">Decision</h3>
                  <p className="acc-text">
                    {record.status === 'approved' ? 'Approved' : 'Declined'} by {record.reviewedByName || 'an administrator'} on{' '}
                    {formatWhen(record.reviewedAt)}.
                  </p>
                  {record.reviewNote && <p className="acc-text">{record.reviewNote}</p>}
                </section>
              )}

              {open && (
                <section className="pgm-section">
                  <h3 className="pgm-section-title">Note to {name}</h3>
                  <textarea
                    className="pg-input pg-textarea"
                    rows={3}
                    maxLength={1000}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Sent with a decline. Optional."
                    aria-label="Note sent with a decline"
                  />
                  <p className="pgm-empty">
                    Approving adds {role} to this user&rsquo;s account straight away. They see it after they
                    reload or sign in again.
                  </p>
                </section>
              )}
            </>
          )}
        </div>

        {/* The decision sits with Close, at the end -- read the request, then
            act on it. Close stays last and is the only filled button. */}
        {/* The done panel replaces the decision once it lands. */}
        {!done && (
          <footer className="pg-modal-foot">
            <div className="pgm-actions">
              {open && (
                <>
                  <button type="button" className="pgm-btn" onClick={() => decide('approve')} disabled={Boolean(busy)}>
                    {busy === 'approve' ? 'Approving…' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    className="pgm-btn pgm-btn--danger"
                    onClick={() => decide('reject')}
                    disabled={Boolean(busy)}
                  >
                    {busy === 'reject' ? 'Declining…' : 'Decline'}
                  </button>
                </>
              )}
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default AccessRequestModal;
